import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteSnippetMutation,
  useGetSnippetQuery
} from '../../../../../services/endpoints/website/snippet';
import { Snippet } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';

export const SnippetDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetSnippetQuery(+params.id!);
  const [snippet, setSnippet] = useState<Snippet>();
  const [deleteSnippet, { isLoading: isDeleting }] = useDeleteSnippetMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading snippet', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setSnippet(data.snippet);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteSnippet(+params.id!).unwrap();
      addToast('Snippet deleted successfully', 'success');
      navigate('/admin/website/snippets');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !snippet) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate('/admin/website/snippets')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Snippets List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Snippet #${snippet?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/website/snippets/${snippet?.id}/edit`)}
          >
            Edit Snippet
          </SimpleButton>
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => setShowModal(true)}
          >
            Delete Snippet
          </SimpleButton>
        </Stack>
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText
              label="Name:"
              text={snippet?.name}
            />
            <LabelText
              label="Content:"
              text={snippet?.content}
            />
            <LabelText
              label="Created At:"
              text={
                snippet?.created_at ? dateToUTC(new Date(snippet?.created_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                snippet?.updated_at ? dateToUTC(new Date(snippet?.updated_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Snippet"
        message="Are you sure you want to delete this snippet?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
